import { useCallback, useContext, useState } from 'react';
import html2canvas from 'html2canvas';

import { LayoutContext } from '../contexts/LayoutContext';

interface ShareImageOptions {
  filename?: string;
  title?: string;
  text?: string;
}

export const useShareImage = (cardRef: React.RefObject<HTMLDivElement | null>, options: ShareImageOptions = {}) => {
  const { isApple } = useContext(LayoutContext);
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    filename = 'beyondegrees-share.png',
    title = 'Share',
    text = '',
  } = options;

  const share = useCallback(async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#E4DFDA',
      });

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/png'),
      );

      if (!blob) return;

      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          ...(!isApple && { title, text }),
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch {
      // User cancelled share dialog or html2canvas error — silently ignore
    } finally {
      setIsGenerating(false);
    }
  }, [cardRef, isApple, filename, title, text]);

  return { share, isGenerating };
};
